import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useWatch, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import RefreshIcon from "@mui/icons-material/Refresh";
import Styled from "./styles";
import { useFeeMapQuery } from "../../../../api/applications.api.slice";
import { useCoreApplicationConfigQuery } from "../../../../api/core.api.slice";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

function FeeResults({ fees }) {
  return (
    <Table size="small" aria-labelledby="fee-details-heading">
      <TableHead>
        <TableRow>
          <TableCell>Fee Description</TableCell>
          <TableCell align="right">Amount</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {fees.map((fee) => (
          <TableRow key={fee.description}>
            <TableCell
              component="th"
              scope="row"
              sx={fee.isSubItem ? { pl: 4, fontSize: "0.85rem" } : undefined}
            >
              {fee.description}
            </TableCell>
            <TableCell
              align="right"
              className="feeValue"
              sx={fee.isSubItem ? { fontSize: "0.85rem" } : undefined}
            >
              <strong>
                {typeof fee.amount === "number"
                  ? formatCurrency(fee.amount)
                  : fee.amount}
              </strong>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function StaticFeesTable() {
  const { applicationType } = useParams();
  const { data: coreAppConfig } = useCoreApplicationConfigQuery();
  const formData = useSelector((state) => state.formProps.formData);
  const application = coreAppConfig?.applications.find(
    (app) => app.applicationType === applicationType
  );
  const applicationFees = application?.fees ?? [];
  const showFeesArray = coreAppConfig.officeInfo?.showFees || [];
  const paymentMethod = useWatch({
    name: "paymentMethod",
    defaultValue: formData?.paymentMethod,
  });
  const renderPaymentTable =
    applicationFees && showFeesArray.includes(paymentMethod);
  if (!renderPaymentTable || applicationFees.length === 0) return null;

  return (
    <Styled>
      <Divider />
      <Box className="table">
        <Typography variant="h6" component="h4">
          Fee Details
        </Typography>
        <Table size="small" aria-label="fee details table">
          <TableHead>
            <TableRow>
              <TableCell component="th" scope="col">
                Item
              </TableCell>
              <TableCell component="th" scope="col" align="right">
                Fee
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applicationFees.map((row) => (
              <TableRow key={row.item}>
                <TableCell component="th" scope="row">
                  {row.item}
                </TableCell>
                <TableCell align="right" className="feeValue">
                  <strong>{row.fee}</strong>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Styled>
  );
}

function DynamicFeesTable() {
  const { applicationType } = useParams();
  const { control } = useFormContext();
  const { data: feeMap, isFetching, isError, refetch } = useFeeMapQuery();
  const { data: coreAppConfig } = useCoreApplicationConfigQuery();
  const formData = useSelector((state) => state.formProps.formData);

  const skusForType = useMemo(() => {
    const skuMap = feeMap?.applicationform;
    if (!skuMap) return [];
    return Object.values(skuMap).filter((sku) =>
      sku.filters?.some((f) => f.applicationType === applicationType)
    );
  }, [feeMap, applicationType]);

  const filterKeys = useMemo(() => {
    const keys = new Set();
    skusForType.forEach((sku) =>
      sku.filters
        ?.filter((f) => f.applicationType === applicationType)
        .forEach((f) => {
          if (f.formProperties) {
            Object.keys(f.formProperties).forEach((k) => keys.add(k));
          }
        })
    );
    return [...keys];
  }, [skusForType, applicationType]);

  const watchedValues = useWatch({
    control,
    name: filterKeys,
    disabled: filterKeys.length === 0,
  });

  const additionalFees = useMemo(() => {
    const application = coreAppConfig?.applications?.find(
      (app) => app.applicationType === applicationType
    );
    if (!application?.fees) return [];
    return application.fees.filter((f) => f.formProperties);
  }, [coreAppConfig, applicationType]);

  const fees = useMemo(() => {
    if (!skusForType.length && !additionalFees.length) return [];
    const isInitialLoad = filterKeys.length > 0 && watchedValues?.length === 0;
    const formValues = {};
    filterKeys.forEach((key, i) => {
      const value = isInitialLoad ? formData?.[key] : watchedValues?.[i];
      if (value != null && value !== "") formValues[key] = value;
    });

    const hasAnyValue = Object.keys(formValues).length > 0;

    const matched = hasAnyValue
      ? skusForType.filter((sku) =>
          sku.filters
            ?.filter((f) => f.applicationType === applicationType)
            .some((f) => {
              if (!f.formProperties) return true;
              return Object.entries(f.formProperties).every(
                ([key, val]) => !(key in formValues) || formValues[key] === val
              );
            })
        )
      : skusForType;

    const result = [];
    matched.forEach((sku) => {
      result.push({
        description: sku.description,
        amount: sku.amount,
      });
      const skuFilter = sku.filters?.find(
        (f) => f.applicationType === applicationType
      );
      const paired = additionalFees.find((f) =>
        Object.entries(f.formProperties).every(
          ([key, val]) => skuFilter?.formProperties?.[key] === val
        )
      );
      if (paired) {
        result.push({
          description: paired.item,
          amount: paired.fee,
          isSubItem: true,
        });
      }
    });

    return result;
  }, [
    skusForType,
    applicationType,
    filterKeys,
    watchedValues,
    additionalFees,
    formData,
  ]);

  if (isFetching) {
    return (
      <Styled>
        <Divider />
        <Box
          className="table"
          aria-busy="true"
          aria-label="Loading fee details"
          sx={{ alignItems: "center" }}
        >
          <CircularProgress size={24} />
        </Box>
      </Styled>
    );
  }

  if (isError) {
    return (
      <Styled>
        <Divider />
        <Box className="table">
          <Alert
            severity="error"
            sx={{
              mb: 2,
              mx: { xs: 2, sm: "auto" },
              width: { xs: "auto", sm: "90%" },
              justifyContent: "center",
            }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={refetch}
              >
                Retry
              </Button>
            }
          >
            Unable to load fee details, please try again.
          </Alert>
        </Box>
      </Styled>
    );
  }

  if (!fees.length) return null;

  return (
    <Styled>
      <Divider />
      <Box
        className="table"
        role="region"
        aria-labelledby="fee-details-heading"
      >
        <Typography variant="h6" id="fee-details-heading" tabIndex="-1">
          Fee Details
        </Typography>
        <FeeResults fees={fees} />
      </Box>
    </Styled>
  );
}

function FeesTable({ isDynamic }) {
  if (!isDynamic) return <StaticFeesTable />;
  return <DynamicFeesTable />;
}

export default FeesTable;
